/**
 * @packageDocumentation
 * This is the file who contains all the logic to handle current active company.
 * It provides a way to switch company
 */

import * as Sentry from '@sentry/react';
import { useSuspenseQuery } from '@tanstack/react-query';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomWithStorage, createJSONStorage } from 'jotai/utils';
import { useCallback } from 'react';
import { To, useNavigate } from 'react-router';
import { fetchCompany } from '~/api/company';
import { RouteConfig } from '~/routes/routeConfig';
import { AnalyticsEvents } from '~/services/analytics';
import { useLoggedUser } from '~/services/user';
import { Company, CompanyQueryKeys } from '~/types/company';
import { getCachedCompanyUserFeatures } from '~/utils/companyUserCache';
import { track } from './segment';

const COMPANY_ID_CACHE_KEY = 'sibill-company-id';
export const COMPANY_SWITCHED_EVENT = 'sibill:company-switched';

const defaultStorage = createJSONStorage<Company['id']>();
defaultStorage.subscribe = undefined;

const activeCompanyIdAtom = atomWithStorage<Company['id']>(COMPANY_ID_CACHE_KEY, '', defaultStorage);

/**
 * Hooks returning atom value for active company id
 *
 * ```tsx
 * const companyId = useActiveCompanyAtomId();
 * ```
 *
 * @category Hook
 */

export const useActiveCompanyId = () => useAtomValue(activeCompanyIdAtom);

const switchCompanyAtom = atom(null, (get, set, companyId: Company['id']) => {
  const activeCompanyId = get(activeCompanyIdAtom);
  if (companyId === activeCompanyId) return;

  set(activeCompanyIdAtom, companyId);
  window.dispatchEvent(new CustomEvent(COMPANY_SWITCHED_EVENT, { detail: { companyId } }));
});

export const useInternalSwitchCompany = () => {
  const switchCompany = useSetAtom(switchCompanyAtom);

  return useCallback(
    (company: Company) => {
      switchCompany(company.id);
    },
    [switchCompany],
  );
};

export const useSwitchCompany = () => {
  const { data: user } = useLoggedUser();
  const current = useActiveCompanyId();
  const navigate = useNavigate();

  return useCallback(
    (company: Company, to: To) => {
      Sentry.setContext('Company', {
        ID: company.id,
        Name: company.name,
        Features: company.featuresV2?.map((feature) => feature.value),
        UserFeatures: user ? getCachedCompanyUserFeatures(company.id, user.id) : undefined,
      });
      track(AnalyticsEvents.COMPANY_SWITCHED, user, company);
      if (current != company.id) {
        navigate(RouteConfig.CompanyRedirect.buildLink({ id: company.id }), { state: to });
      }
    },
    [current, user, navigate],
  );
};

export const useClearCompany = () => {
  return () => {
    Sentry.setContext('Company', null);
  };
};

export const useActiveCompany = () => {
  const companyId = useActiveCompanyId();
  const { data: company } = useSuspenseCompany(companyId);
  return company;
};

const useSuspenseCompany = (id: Company['id']) => {
  return useSuspenseQuery({
    queryKey: CompanyQueryKeys.company(id),
    queryFn: () => fetchCompany(id),
    select: ({ company }) => company,
    staleTime: Infinity,
  });
};
